import { useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'

/**
 * Hook to check user permissions
 *
 * Usage:
 * const canViewCost = usePermission('products.view_cost')
 * const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions()
 */

export const usePermission = (permission: string): boolean => {
  const user = useAuthStore((state) => state.user)

  return useMemo(() => {
    if (!user || !user.permissions) {
      return false
    }

    return user.permissions.includes(permission)
  }, [user, permission])
}

export const usePermissions = () => {
  const user = useAuthStore((state) => state.user)

  const hasPermission = useMemo(
    () => (permission: string): boolean => {
      if (!user || !user.permissions) {
        return false
      }
      return user.permissions.includes(permission)
    },
    [user]
  )

  const hasAnyPermission = useMemo(
    () => (permissions: string[]): boolean => {
      if (!user || !user.permissions) {
        return false
      }
      return permissions.some(permission => user.permissions!.includes(permission))
    },
    [user]
  )

  const hasAllPermissions = useMemo(
    () => (permissions: string[]): boolean => {
      if (!user || !user.permissions) {
        return false
      }
      return permissions.every(permission => user.permissions!.includes(permission))
    },
    [user]
  )

  const permissions = useMemo(() => user?.permissions || [], [user])

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    permissions,
    user
  }
}

// Role-based checks (for backward compatibility or simple role checks)
export const useRole = () => {
  const user = useAuthStore((state) => state.user)

  const isAdmin = useMemo(() => user?.role === 'admin', [user])
  const isManager = useMemo(() => user?.role === 'manager', [user])
  const isCashier = useMemo(() => user?.role === 'cashier', [user])

  const hasRole = useMemo(
    () => (role: 'admin' | 'manager' | 'cashier'): boolean => {
      return user?.role === role
    },
    [user]
  )

  return {
    role: user?.role,
    roleName: user?.role_name || user?.role,
    isAdmin,
    isManager,
    isCashier,
    hasRole
  }
}
